import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { siteComplianceApi, type SiteComplianceApiError } from '@/lib/siteComplianceApi';

export type SiteComplianceRole = 'SiteCompliance.CheckpointManager';

export interface RoleGuardState {
  disabled: boolean;
  isAllowed: boolean;
  isLoading: boolean;
  tooltip: string | null;
}

export const siteComplianceRoleGuardKey = (role: SiteComplianceRole) =>
  ['site-compliance', 'role-guard', role] as const;

export const markSiteComplianceRoleDenied = (
  queryClient: QueryClient,
  role: SiteComplianceRole,
) => {
  queryClient.setQueryData(siteComplianceRoleGuardKey(role), false);
};

export const createSiteComplianceGetRoleProbe =
  (url: string, params?: Record<string, unknown>) => () =>
    siteComplianceApi.get(url, { params });

export const useInferredSiteComplianceRole = ({
  probe,
  role,
}: {
  probe: () => Promise<unknown>;
  role: SiteComplianceRole;
}) =>
  useQuery({
    gcTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      try {
        await probe();
        return true;
      } catch (error) {
        if ((error as SiteComplianceApiError).response?.status === 403) {
          return false;
        }

        return true;
      }
    },
    queryKey: siteComplianceRoleGuardKey(role),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useMarkSiteComplianceRoleDenied = () => {
  const queryClient = useQueryClient();

  return (role: SiteComplianceRole) => {
    markSiteComplianceRoleDenied(queryClient, role);
  };
};
